"use client";

import { useState } from "react";
import Link from "next/link";
import { Award } from "lucide-react";

import { SuggestCertRequestExplainer } from "@/components/suggest-cert-request-explainer";
import { SuggestCertificationForm } from "@/components/suggest-certification-form";
import { useToast } from "@/hooks/use-toast";

interface SuggestUnknownCertificationProps {
  initialName: string;
}

export function SuggestUnknownCertification({
  initialName,
}: SuggestUnknownCertificationProps) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="container mx-auto px-4 py-10 sm:py-16 max-w-xl">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
        <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
          <Award className="h-6 w-6 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          We don&apos;t have a page for this certification yet
        </h1>
        {submitted ? (
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Thanks — we received your request. If we add this certification,
            it will get an exam page, study groups, and a place in the
            community. You can keep browsing other certifications in the
            meantime.
          </div>
        ) : (
          <>
            <SuggestCertRequestExplainer certName={initialName} />
            <SuggestCertificationForm
              initialName={initialName}
              submitLabel="Request this certification"
              onSuccess={() => {
                setSubmitted(true);
                toast({
                  title: "Request submitted",
                  description:
                    "Thanks — we'll review adding this certification so people can study for it on CertLynx.",
                });
              }}
            />
          </>
        )}

        <p className="mt-6 text-sm text-gray-500">
          Looking for a certification we already list?{" "}
          <Link href="/certifications" className="text-blue-600 hover:underline font-medium">
            Browse certifications
          </Link>
        </p>
      </div>
    </div>
  );
}
